import { Icon } from '@/modules/Icon';
import { IconAvatarProps } from '../types';
import { getBaseClasses, getColorClasses } from '../utils';
import { ICON_SIZES, DEFAULT_ICON, DEFAULT_COLOR, DEFAULT_THEME, DEFAULT_SIZE } from '../constants';
import { cn } from '@/shared/utils/cn';
import { AvatarStatus } from './AvatarStatus';
import { IconFontSize, IconType, AnyIconDefinition } from '@/shared/types/types';

/**
 * ============================================
 * ICON AVATAR - ATOMIC COMPONENT
 * ============================================
 * Avatar que muestra un icono (por defecto UserOutlined, pero personalizable)
 *
 * @component
 * @example
 * ```tsx
 * <IconAvatar
 *   theme="solid"
 *   color="blue"
 *   size="md"
 * />
 *
 * // Con icono personalizado
 * <IconAvatar
 *   theme="solid"
 *   color="blue"
 *   size="md"
 *   icon={faBell}
 * />
 * ```
 */
export const IconAvatar = ({
  theme = DEFAULT_THEME,
  color = DEFAULT_COLOR,
  size = DEFAULT_SIZE,
  stroke = false,
  pill = true,
  disabled = false,
  grayscaleWhenDisabled = true,
  className,
  icon,
  status,
}: IconAvatarProps) => {
  // Obtener clases de color según el tema
  const colorClasses = getColorClasses(color, theme, 'icon', disabled);

  // Obtener tamaño del icono proporcional al tamaño del avatar
  const iconSize = ICON_SIZES[size];

  // Mapear tamaño del avatar al tamaño del status (solo si no se especifica en status)
  const statusSizeMap: Record<string, 'sm' | 'md' | 'lg'> = {
    xxs: 'sm',
    xs: 'sm',
    sm: 'sm',
    md: 'md',
    lg: 'md',
    xl: 'lg',
  };
  // Usar el size del status si está especificado, sino usar el mapeo automático
  const statusSize = status?.size || statusSizeMap[size] || 'md';

  return (
    <div className="ui-imj ui-relative ui-inline-flex">
      <div
        className={cn(
          getBaseClasses(
            size,
            stroke,
            pill,
            disabled,
            grayscaleWhenDisabled,
            className,
            'icon',
            color,
            theme,
          ),
          colorClasses,
        )}
      >
        <Icon
          name={(icon || DEFAULT_ICON) as IconType | AnyIconDefinition}
          size={iconSize as IconFontSize}
          color="current"
        />
      </div>
      {status && <AvatarStatus {...status} size={statusSize} />}
    </div>
  );
};
